import { Timestamp } from "firebase/firestore";

import {
  getEntryLoggedAt,
  getEntryLoggedDate,
} from "../../../../pages/callingWorklist/services/callingHistoryFields";
import { CallingHistoryLog } from "../../../../pages/callingWorklist/services/callingHistoryService";
import { EMPTY_FIELD_VALUE } from "./getSecondStpStatusLabel";

/** Eastern time zone the calling worklist reports call times in. */
const EASTERN_TIME_ZONE = "America/New_York";

/**
 * Converts a Firestore timestamp field into a Date.
 *
 * @input value - Firestore Timestamp, Date, or parseable date string
 * @return Date instance, or null when the value cannot be parsed
 */
const toDate = (value: Timestamp | Date | string | undefined): Date | null => {
  if (!value) {
    return null;
  }

  const date =
    value instanceof Timestamp
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);

  return isNaN(date.getTime()) ? null : date;
};

/**
 * Formats a history entry's creation time as `11:11 AM - 07/14/2026`, falling
 * back to the stored call timestamp for entries written before it was recorded.
 *
 * @input entry - Calling history log entry
 * @return Display timestamp for the entry card header
 */
export const formatHistoryTimestamp = (entry: CallingHistoryLog): string => {
  const date = toDate(entry.created_at);
  if (!date) {
    return (
      getEntryLoggedAt(entry).trim() ||
      getEntryLoggedDate(entry) ||
      EMPTY_FIELD_VALUE
    );
  }

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EASTERN_TIME_ZONE,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);

  const getPart = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";

  const time = `${getPart("hour")}:${getPart("minute")} ${getPart("dayPeriod")}`;
  const day = `${getPart("month")}/${getPart("day")}/${getPart("year")}`;

  return `${time} - ${day}`;
};
